import { EAnalyticsTour } from "@frontend-shared/product-analytics";
import { useDirection } from "@radix-ui/react-direction";
import type { PropsWithChildren, ReactNode } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { Step } from "react-joyride";
import { useLocation, useNavigate } from "react-router-dom";

import { useCompleteCaTour } from "@/api/user/useCompleteCaTour";
import { PATHS } from "@/constants/paths";
import {
  CA_TOUR_ROWS,
  NAVIGATION_DELAY_MS,
  TOUR_PAGE_READY_SELECTOR,
  TOUR_STEP_ROUTE_MAP,
  TOUR_TARGET_WAIT_MS,
  UPLOADS_TOUR_ROWS,
} from "@/constants/tour";
import { CallAssistantTourContext } from "@/contexts/CallAssistantTourContext";
import { useAuth } from "@/hooks/use-auth";
import { useCallAssistantFeaturesEnabled } from "@/hooks/use-call-assistant-features-enabled";
import { useTourJoyrideCallback } from "@/hooks/use-tour-joyride-callback";
import { useTourLifecycle } from "@/hooks/use-tour-lifecycle";

import { TourShell } from "./TourShell";
import type { IStepNavigationProps } from "./types";
import {
  createTourStepBuilder,
  pollForTargetAndResume,
  scrollStepTargetIntoView,
} from "./utils";

const CallAssistantTourProvider = ({
  children,
}: PropsWithChildren): ReactNode => {
  const { t } = useTranslation("tour");
  const dir = useDirection();
  const navigate = useNavigate();
  const location = useLocation();
  const { mutate: completeCaTour } = useCompleteCaTour();
  const { user } = useAuth();
  const isCallAssistantFeaturesEnabled = useCallAssistantFeaturesEnabled();

  const [isNavigating, setIsNavigating] = useState(false);
  const navigationTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  // NOTE: React Query's optimistic `setQueryData` in `onMutate` doesn't
  // always batch with `setIsWelcomeOpen(false)`, so the auto-fire effect
  // can briefly see `isWelcomeOpen=false` + `isCaTourCompleted=false` and
  // re-open. This ref blocks the re-open after an explicit dismissal.
  const hasBeenDismissedRef = useRef(false);

  const sidebarPlacement = dir === "rtl" ? "left" : "right";

  const steps: Step[] = useMemo(() => {
    const createStep = createTourStepBuilder(sidebarPlacement);

    return [
      ...CA_TOUR_ROWS.map(([target, suffix, placement]) =>
        createStep({
          target,
          title: t(`steps.callAssistant${suffix}.title`),
          content: t(`steps.callAssistant${suffix}.description`),
          placement,
        }),
      ),
      ...UPLOADS_TOUR_ROWS.map(([target, suffix, placement, floaterProps]) =>
        createStep({
          target,
          title: t(`steps.uploads${suffix}.title`),
          content: t(`steps.uploads${suffix}.description`),
          placement,
          floaterProps,
        }),
      ),
    ];
  }, [sidebarPlacement, t]);

  const clearNavigationState = useCallback((): void => {
    setIsNavigating(false);
    clearTimeout(navigationTimeoutRef.current);
  }, []);

  const handleStartTour = useCallback((): void => {
    clearNavigationState();
    hasBeenDismissedRef.current = false;
  }, [clearNavigationState]);

  const handleWelcomeDismiss = useCallback((): void => {
    hasBeenDismissedRef.current = true;
  }, []);

  const handleResetTour = useCallback((): void => {
    hasBeenDismissedRef.current = true;
    clearNavigationState();
  }, [clearNavigationState]);

  const {
    isWelcomeOpen,
    setIsWelcomeOpen,
    isRunning,
    currentStep,
    setCurrentStep,
    startTour,
    handleStartActualTour,
    resetTour,
    handleWelcomeClose,
  } = useTourLifecycle({
    tour: EAnalyticsTour.CALL_ASSISTANT,
    isCompleted: user?.isCaTourCompleted,
    completeTour: completeCaTour,
    onStartTour: handleStartTour,
    onResetTour: handleResetTour,
    onWelcomeClose: handleWelcomeDismiss,
  });

  const scheduleDelayedResume = useCallback(
    (stepIndex: number): void => {
      setIsNavigating(true);
      setCurrentStep(stepIndex);
      clearTimeout(navigationTimeoutRef.current);
      navigationTimeoutRef.current = setTimeout(() => {
        const target = steps[stepIndex]?.target as string | undefined;
        const requiredRoute = target ? TOUR_STEP_ROUTE_MAP[target] : undefined;
        const pageReadySelector = requiredRoute
          ? TOUR_PAGE_READY_SELECTOR[requiredRoute]
          : undefined;

        pollForTargetAndResume({
          selectorToPoll: pageReadySelector ?? target,
          deadline: Date.now() + TOUR_TARGET_WAIT_MS,
          stepTarget: (steps[stepIndex]?.target as string) ?? "",
          navigationTimeoutRef,
          onResumed: () => {
            scrollStepTargetIntoView({ steps, stepIndex });
            setIsNavigating(false);
          },
        });
      }, NAVIGATION_DELAY_MS);
    },
    [steps, setCurrentStep],
  );

  const navigateToStepRoute = useCallback(
    (nextIndex: number): boolean => {
      const nextTarget = steps[nextIndex]?.target as string | undefined;

      if (!nextTarget) return false;

      const requiredRoute = TOUR_STEP_ROUTE_MAP[nextTarget];

      if (requiredRoute && location.pathname !== requiredRoute) {
        navigate(requiredRoute);
        scheduleDelayedResume(nextIndex);
        return true;
      }

      return false;
    },
    [location.pathname, navigate, scheduleDelayedResume, steps],
  );

  const handleStepNavigation = useCallback(
    ({ nextIndex }: IStepNavigationProps): void => {
      const hasNavigated = navigateToStepRoute(nextIndex);

      if (hasNavigated) return;

      // NOTE: Same-page steps need no route change, so Joyride finds the target
      // synchronously — just scroll it into view and advance.
      scrollStepTargetIntoView({ steps, stepIndex: nextIndex });
      setCurrentStep(nextIndex);
    },
    [navigateToStepRoute, steps, setCurrentStep],
  );

  const handleJoyrideCallback = useTourJoyrideCallback({
    steps,
    handleStepNavigation,
    resetTour,
    tour: EAnalyticsTour.CALL_ASSISTANT,
    isRunning,
    onTargetNotFound: scheduleDelayedResume,
  });

  // NOTE: react-joyride only retries a not-found target inside its own
  // componentDidUpdate, never on the initial mount — so if step 0's target
  // isn't resolvable on that first synchronous render (a real, observed race
  // with no error/warning surfaced), the tour permanently renders nothing,
  // since nothing ever triggers a second render to give it another chance.
  // Proactively schedule the same pause/resume cycle used for the reactive
  // (post-navigation) case so Joyride gets a real update once the DOM has
  // settled, instead of relying solely on a callback it structurally can't
  // fire on a first-render failure.
  const handleStartActualTourWithRetry = useCallback((): void => {
    handleStartActualTour();
    scheduleDelayedResume(0);
  }, [handleStartActualTour, scheduleDelayedResume]);

  useEffect(() => {
    return () => {
      clearTimeout(navigationTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (hasBeenDismissedRef.current) return;
    if (isRunning || isWelcomeOpen || !user || !isCallAssistantFeaturesEnabled)
      return;
    if (user.isCaTourCompleted) return;
    // NOTE: Defer CA welcome until the main tour is done. The main tour's
    // auto-fire doesn't gate by route, so without this both welcome dialogs
    // would stack on top of each other when a fresh user lands directly on
    // /call-assistant.
    if (!user.isTourCompleted) return;
    if (location.pathname !== PATHS.CALL_ASSISTANT) return;

    setIsWelcomeOpen(true);
  }, [
    isRunning,
    isWelcomeOpen,
    user,
    isCallAssistantFeaturesEnabled,
    location.pathname,
    setIsWelcomeOpen,
  ]);

  const currentStepTarget = (steps[currentStep]?.target as string) ?? "";

  const tourContextValue = useMemo(
    () => ({
      startTour,
      currentStepTarget,
      isRunning,
    }),
    [startTour, currentStepTarget, isRunning],
  );

  return (
    <CallAssistantTourContext.Provider value={tourContextValue}>
      <TourShell
        isWelcomeOpen={isWelcomeOpen}
        onStartTour={handleStartActualTourWithRetry}
        onWelcomeClose={handleWelcomeClose}
        welcomeTitleKey="callAssistantWelcome.title"
        welcomeDescriptionKey="callAssistantWelcome.description2"
        joyride={{
          steps,
          stepIndex: currentStep,
          isRunning,
          isPaused: isNavigating,
          callback: handleJoyrideCallback,
        }}
      >
        {children}
      </TourShell>
    </CallAssistantTourContext.Provider>
  );
};

export default CallAssistantTourProvider;
